import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useOnboarding } from './OnboardingProvider';
import {
  clearPostAuthTarget,
  readPostAuthTargetAsync,
} from '../../lib/auth-handoff';
import { useClaimCvHandoff } from '../../hooks/useTailoredCvApi';
import { useToast } from '../../hooks/useToast';
import {
  resolveCvHandoffClaimOutcome,
  resolveCvHandoffErrorCopy,
} from '../../lib/cv-handoff-error';
import { trackProductEvent } from '../../lib/product-analytics';

/**
 * Auto-claim of a pending CV-handoff invite on the ai-input step, mirroring
 * the resume-handoff auto-import precedent in StepAIInput(.web).tsx.
 *
 * A recipient without an account signs UP from the invite link, so the
 * `cv-handoff:<token>` post-auth target survives onboarding (see
 * lib/auth-handoff.ts) and lands here. The claim uses intent 'update': the new
 * account's profile is empty, and the server hydrates it from the delivered
 * CV, so the onboarding CV preview shows exactly what was sent. On success the
 * provider switches to the short claim flow (preview -> public details ->
 * privacy -> done); on failure a toast explains why and the NORMAL flow
 * continues from ai-input, with the invite kept only while it may still be
 * claimable (see the catch below).
 */
export function useOnboardingCvHandoffClaim() {
  const { t } = useTranslation(['auth', 'seeker']);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { enterClaimedCvFlow } = useOnboarding();
  const claimHandoff = useClaimCvHandoff();
  const [isClaimingCvHandoff, setIsClaimingCvHandoff] = useState(false);
  const attemptedRef = useRef(false);

  // Refs so the one-shot effect reads the latest callbacks without re-running.
  const claimRef = useRef(claimHandoff.mutateAsync);
  claimRef.current = claimHandoff.mutateAsync;
  const enterClaimedCvFlowRef = useRef(enterClaimedCvFlow);
  enterClaimedCvFlowRef.current = enterClaimedCvFlow;

  useEffect(() => {
    if (attemptedRef.current) {
      return;
    }

    attemptedRef.current = true;

    void readPostAuthTargetAsync().then((target) => {
      if (!target.startsWith('cv-handoff:')) {
        return;
      }

      const token = target.slice('cv-handoff:'.length);
      setIsClaimingCvHandoff(true);
      trackProductEvent('cv_handoff_claim_started_client', {
        route: '/onboarding/ai-input',
        context: 'onboarding',
        intent: 'update',
      });
      return claimRef
        .current({ token, intent: 'update' })
        .then(async (result) => {
          // The claim committed either way, so the invite is spent.
          clearPostAuthTarget();

          // ...but the profile write behind intent 'update' can fail after it.
          // The claimed copy is in the account and will be there once
          // onboarding ends, while the profile the CV preview renders from was
          // never written: entering the claim flow would show an empty preview
          // and skip the steps that fill one in. So stay in the NORMAL flow and
          // say where the delivered CV went.
          const outcome = resolveCvHandoffClaimOutcome(result);
          if (outcome.kind === 'profile-not-updated') {
            toast.show({
              title: t('seeker:cv.handoff.claimProfileNotUpdatedTitle'),
              message: t(
                'auth:onboarding.cvJourney.cvHandoffProfileNotUpdatedBody',
                {
                  defaultValue:
                    'The CV you were sent is saved to your account. We could not fill your profile from it, so build your CV below and the saved copy will be waiting.',
                },
              ),
              type: 'info',
            });
            return;
          }

          // The server replaced the (empty) master profile with the delivered
          // CV: refetch onboarding state/profile so the provider's hydration
          // effect fills the local draft the preview renders from.
          await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
          enterClaimedCvFlowRef.current();
        })
        .catch((error) => {
          const copy = resolveCvHandoffErrorCopy(error);
          // A recognized guard (expired, wrong email, already owner, cancelled)
          // can never pass, so consume the invite: left stored, the dead token
          // would be resumed again when onboarding completes. An unrecognized
          // failure (offline, 5xx, a code this build has not seen) may well be
          // transient, so the token stays and completion hands the recipient to
          // the /cv-handoff chooser, where they can retry or leave it.
          if (!copy.isRetryable) {
            clearPostAuthTarget();
          }
          toast.error(
            t('auth:onboarding.cvJourney.cvHandoffClaimFailedTitle', {
              defaultValue: 'Could not open the CV you were sent',
            }),
            // The server's message is hard-coded English, so the reason is read
            // from the error CODE and rendered from the shared localized copy.
            // Only a failure with no recognized code falls back to the generic
            // onboarding sentence, which is the one that fits it.
            copy.code
              ? t(`seeker:${copy.bodyKey}`, copy.bodyParams)
              : t('auth:onboarding.cvJourney.cvHandoffClaimFailedBody', {
                  defaultValue:
                    'We could not reach the CV you were sent. You can keep building your CV below, and we will try the invite again when you finish.',
                }),
          );
        })
        .finally(() => {
          setIsClaimingCvHandoff(false);
        });
    });
  }, [queryClient, t, toast]);

  return { isClaimingCvHandoff };
}
